
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/ui/image-upload";
import { ColorPicker } from "@/components/ui/color-picker";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormError } from "@/components/form-error";
import { SubmitButton } from "@/components/submit-button";
import { ServicePreview } from "@/components/service/service-preview";
import { PageHeader } from "@/components/layout/page-header";
import {
  useCreateServiceMutation,
  useUpdateServiceMutation,
} from "@/hooks/use-services";
import { uploadImage } from "@/lib/api/images";
import {
  serviceCreateSchema,
  serviceEditSchema,
  normalizeServiceUrl,
  type ServiceFormData,
} from "@/lib/validations/service";
import { createDataSource, createDataSourceSnapshot, previewDataSource } from "@/lib/api/data-sources";
import { createWidgetTemplate } from "@/lib/api/templates";
import { createWidget } from "@/lib/api/widgets";
import { createApiKey } from "@/lib/api/api-keys";
import { useServiceStore } from "@/stores/service-store";
import type { Service } from "@/types/service";

interface ServiceFormProps {
  mode: "create" | "edit";
  service?: Service;
  onSuccess?: (serviceId: string) => void;
  hideHeader?: boolean;
}

export function ServiceForm({ mode, service, onSuccess, hideHeader }: ServiceFormProps) {
  const navigate = useNavigate();
  const { setSelectedService } = useServiceStore();
  const createMutation = useCreateServiceMutation();
  const updateMutation = useUpdateServiceMutation();
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [logoCleared, setLogoCleared] = useState(false);
  const [bannerCleared, setBannerCleared] = useState(false);

  const defaultValues: ServiceFormData =
    mode === "edit" && service
      ? {
          title: service.title || "",
          description: service.description || "",
          url: service.url || "",
          text_color: service.text_color || "",
          background_color: service.background_color || "",
          accent_color: service.accent_color || "",
          logo: null,
          banner: null,
        }
      : {
          title: "",
          description: "",
          url: "",
          text_color: "",
          background_color: "",
          accent_color: "",
          logo: null,
          banner: null,
        };

  const form = useForm<ServiceFormData>({
    resolver: zodResolver(mode === "create" ? serviceCreateSchema : serviceEditSchema),
    defaultValues,
  });

  const watchedValues = useWatch({ control: form.control });

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(
    mode === "edit" ? service?.logo_url : undefined
  );
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | undefined>(
    mode === "edit" ? service?.banner_url : undefined
  );

  useEffect(() => {
    if (watchedValues.logo instanceof File) {
      const url = URL.createObjectURL(watchedValues.logo);
      setLogoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (logoCleared || !watchedValues.logo) {
      if (mode === "edit" && service?.logo_url && !logoCleared) {
        setLogoPreviewUrl(service.logo_url);
      } else {
        setLogoPreviewUrl(undefined);
      }
    }
  }, [watchedValues.logo, logoCleared, mode, service?.logo_url]);

  useEffect(() => {
    if (watchedValues.banner instanceof File) {
      const url = URL.createObjectURL(watchedValues.banner);
      setBannerPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (bannerCleared || !watchedValues.banner) {
      if (mode === "edit" && service?.banner_url && !bannerCleared) {
        setBannerPreviewUrl(service.banner_url);
      } else {
        setBannerPreviewUrl(undefined);
      }
    }
  }, [watchedValues.banner, bannerCleared, mode, service?.banner_url]);

  const previewData = useMemo(() => ({
    title: watchedValues.title || "",
    description: watchedValues.description || "",
    url: watchedValues.url || "",
    text_color: watchedValues.text_color || "",
    background_color: watchedValues.background_color || "",
    accent_color: watchedValues.accent_color || "",
    logo_url: logoPreviewUrl,
    banner_url: bannerPreviewUrl,
  }), [watchedValues, logoPreviewUrl, bannerPreviewUrl]);

  useEffect(() => {
    if (mode === "edit" && service) {
      form.reset({
        title: service.title || "",
        description: service.description || "",
        url: service.url || "",
        text_color: service.text_color || "",
        background_color: service.background_color || "",
        accent_color: service.accent_color || "",
        logo: null,
        banner: null,
      });
      setLogoCleared(false);
      setBannerCleared(false);
    }
  }, [service, form, mode]);

  // The whole of onSubmit, including the default resources created after
  // the service itself in create mode.
  const { isSubmitting } = form.formState;

  const clearApiError = () => {
    if (createMutation.isError) {
      createMutation.reset();
    }
    if (updateMutation.isError) {
      updateMutation.reset();
    }
  };

  const onSubmit = async (data: ServiceFormData) => {
    createMutation.reset();
    updateMutation.reset();

    const hasExistingLogo = service?.logo_url && !logoCleared;
    const hasNewLogo = data.logo instanceof File;
    if (!hasExistingLogo && !hasNewLogo) {
      form.setError("logo", { message: "Logo is required" });
      return;
    }

    try {
      let serviceId: string;

      const normalizedUrl = normalizeServiceUrl(data.url || "");
      // Edit sends "" to clear a value; create omits empty values.
      const basicPayload = mode === "edit"
        ? {
            title: data.title,
            description: data.description || "",
            url: normalizedUrl,
            text_color: data.text_color || "",
            background_color: data.background_color || "",
            accent_color: data.accent_color || "",
          }
        : {
            title: data.title,
            description: data.description || undefined,
            url: normalizedUrl || undefined,
            text_color: data.text_color || undefined,
            background_color: data.background_color || undefined,
            accent_color: data.accent_color || undefined,
          };

      if (mode === "create") {
        const response = await createMutation.mutateAsync(basicPayload);
        serviceId = response.data.id;
      } else {
        serviceId = service!.id;
        await updateMutation.mutateAsync({ id: serviceId, data: basicPayload });
      }

      // Pass serviceId explicitly: for a new service the selected service in
      // localStorage hasn't been updated yet.
      let logoUrl: string | undefined;
      let bannerUrl: string | undefined;

      if (data.logo) {
        setIsUploadingLogo(true);
        try {
          const result = await uploadImage(data.logo, { serviceId });
          logoUrl = result.url;
        } catch {
          form.setError("logo", { message: "Failed to upload logo" });
          setIsUploadingLogo(false);
          return;
        }
        setIsUploadingLogo(false);
      }

      if (data.banner) {
        setIsUploadingBanner(true);
        try {
          const result = await uploadImage(data.banner, { serviceId });
          bannerUrl = result.url;
        } catch {
          form.setError("banner", { message: "Failed to upload banner" });
          setIsUploadingBanner(false);
          return;
        }
        setIsUploadingBanner(false);
      }

      const hasImageChanges = logoUrl || bannerUrl || logoCleared || bannerCleared;
      if (hasImageChanges) {
        await updateMutation.mutateAsync({
          id: serviceId,
          data: {
            ...(logoUrl ? { logo_url: logoUrl } : logoCleared ? { logo_url: "" } : {}),
            ...(bannerUrl ? { banner_url: bannerUrl } : bannerCleared ? { banner_url: "" } : {}),
          },
        });
      }

      if (mode === "create") {
        try {
          const apiKeyResponse = await createApiKey({ name: "Default" });
          const apiKey = apiKeyResponse.data.api_key;

          const dataSourceUrl = `${import.meta.env.VITE_API_URL}/v1/services/${serviceId}`;
          const dataSourceHeaders = {
            "X-Service-Id": serviceId,
            "Authorization": `Bearer ${apiKey}`,
          };

          const previewResponse = await previewDataSource(
            {
              name: "Service",
              url: dataSourceUrl,
              user_id: "test_id",
              query_param: true,
              header: true,
              headers: dataSourceHeaders,
            },
            { serviceId }
          );
          const snapshot =
            previewResponse.data.valid &&
            previewResponse.data.code >= 200 &&
            previewResponse.data.code < 300
              ? previewResponse.data.body
              : "{}";

          const dataSourceResponse = await createDataSource(
            {
              name: "Service",
              type: "url",
              definition: {
                url: dataSourceUrl,
                user_id: "test_id",
                query_param: true,
                header: true,
                headers: dataSourceHeaders,
              },
            },
            { serviceId }
          );

          await createDataSourceSnapshot(
            dataSourceResponse.data.id,
            { user_id: "test_id", snapshot },
            { serviceId }
          );

          const iosTemplate = JSON.stringify(
            {
              variants: [
                {
                  properties: {
                    version: "1.0",
                    supportedFamilies: [
                      "systemSmall",
                      "systemMedium",
                      "systemLarge",
                      "systemExtraLarge",
                    ],
                    padding: {
                        top: 20,
                        bottom: 20,
                        left: 20,
                        right: 20,
                    },
                    url: "{{$.url}}"
                  },
                  elements: [
                    {
                      type: "vstack",
                      properties: {},
                      elements: [
                        {
                          type: "image",
                          properties: {
                            src: "{{$.logo_url}}",
                            cornerRadius: 10,
                            contentMode: "fit"
                          },
                        },
                        {
                          type: "text",
                          properties: {
                            text: "{{$.title}}"
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  properties: {
                    version: "1.0",
                    supportedFamilies: [
                      "accessoryCircular",
                    ],
                    url: "{{$.url}}"
                  },
                  elements: [
                    {
                      type: "image",
                      properties: {
                        src: "{{$.logo_url}}",
                        cornerRadius: 10,
                        contentMode: "fit"
                      },
                    },
                  ],
                },
                {
                  properties: {
                    version: "1.0",
                    supportedFamilies: [
                      "accessoryInline",
                    ],
                    url: "{{$.url}}"
                  },
                  elements: [
                    {
                      type: "text",
                      properties: {
                        text: "{{$.title}}"
                      },
                    },
                  ],
                },
                {
                  properties: {
                    version: "1.0",
                    supportedFamilies: [
                      "accessoryRectangular",
                    ],
                    url: "{{$.url}}"
                  },
                  elements: [
                    {
                      type: "image",
                      properties: {
                        src: "{{$.logo_url}}",
                        cornerRadius: 10,
                        contentMode: "fit"
                      },
                    },
                    {
                      type: "text",
                      properties: {
                        text: "{{$.title}}"
                      },
                    },
                  ],
                },
                {
                  properties: {
                    version: "1.0",
                    supportedFamilies: [
                      "controlWidget",
                    ],
                  },
                  elements: [
                    {
                      type: "controlWidget",
                      properties: {
                        label: "{{$.title}}",
                        url: "{{$.url}}",
                        symbol: "button.programmable"
                      },
                    },
                  ],
                },
              ],
            },
            null,
            2
          );
          const androidTemplate = JSON.stringify(
            {
              variants: [
                {
                  properties: {
                    version: "1.0",
                    padding: {
                        top: 20,
                        bottom: 20,
                        left: 20,
                        right: 20,
                    },
                    url: "{{$.url}}",
                  },
                  elements: [
                    {
                      type: "column",
                      properties: {},
                      elements: [
                        {
                          type: "image",
                          properties: {
                            src: "{{$.logo_url}}",
                            cornerRadius: 10,
                            contentMode: "fit"
                          },
                        },
                        {
                          type: "text",
                          properties: {
                            text: "{{$.title}}",
                            padding: {
                              top: 10
                            },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            null,
            2
          );
          const watchosTemplate = JSON.stringify(
              {
                variants: [
                  {
                    properties: {
                      version: "1.0",
                      supportedFamilies: [
                        "accessoryCircular",
                      ],
                      label: {text: "{{$.title}}"},
                      url: "{{$.url}}"
                    },
                    elements: [
                      {
                        type: "image",
                        properties: {
                          src: "{{$.logo_url}}",
                          cornerRadius: 10,
                          contentMode: "fit"
                        },
                      },
                    ],
                  },
                  {
                    properties: {
                      version: "1.0",
                      supportedFamilies: [
                        "accessoryCorner",
                      ],
                      label: {text: "{{$.title}}"},
                      url: "{{$.url}}"
                    },
                    elements: [
                      {
                        type: "image",
                        properties: {
                          src: "{{$.logo_url}}",
                          cornerRadius: 10,
                          contentMode: "fit"
                        },
                      },
                    ],
                  },
                  {
                    properties: {
                      version: "1.0",
                      supportedFamilies: [
                        "accessoryInline",
                      ],
                      label: {text: "{{$.title}}"},
                      url: "{{$.url}}"
                    },
                    elements: [
                      {
                        type: "text",
                        properties: {
                          text: "{{$.title}}"
                        },
                      },
                    ],
                  },
                  {
                    properties: {
                      version: "1.0",
                      supportedFamilies: [
                        "accessoryRectangular",
                      ],
                      label: {text: "{{$.title}}"},
                      url: "{{$.url}}"
                    },
                    elements: [
                      {
                        type: "image",
                        properties: {
                          src: "{{$.logo_url}}",
                          cornerRadius: 10,
                          contentMode: "fit"
                        },
                      },
                    ],
                  },
                ],
              },
              null,
              2
          );
          const templateResponse = await createWidgetTemplate(
            {
              name: "Preview Widget",
              template_ios: iosTemplate,
              template_android: androidTemplate,
              template_watchos: watchosTemplate,
            },
            { serviceId }
          );

          await createWidget(
            {
              name: "Preview",
              data_source_id: dataSourceResponse.data.id,
              template_id: templateResponse.data.id,
              published: true,
            },
            { serviceId }
          );

        } catch {
          // Non-critical: the service itself was created.
          console.warn("Failed to create default API key, data source, or widget");
        }
      }

      // Selecting it sets the X-Service-Id header for subsequent API calls.
      setSelectedService({
        id: serviceId,
        title: data.title,
        logo_url: logoUrl ?? (logoCleared ? undefined : service?.logo_url),
      } as Service);

      if (onSuccess) {
        onSuccess(serviceId);
      } else {
        navigate("/");
      }
    } catch {
      // Error is handled by mutation state
    }
  };

  const isCreate = mode === "create";
  const pageTitle = isCreate ? "Create Service" : "Edit Service";
  const submitButtonText = isCreate ? "Create service" : "Save changes";
  const submittingButtonText = isCreate ? "Creating..." : "Saving...";

  const mutationError = createMutation.error || updateMutation.error;
  const hasMutationError = createMutation.isError || updateMutation.isError;

  const submitButton = (
    <SubmitButton
      form="service-form"
      pending={isSubmitting}
      pendingText={submittingButtonText}
    >
      {submitButtonText}
    </SubmitButton>
  );

  return (
    <div className="flex flex-1 flex-col gap-6">
      {!hideHeader && (
        <PageHeader title={pageTitle} actions={submitButton} />
      )}

      <div className="mx-auto grid w-full max-w-5xl gap-6 lg:grid-cols-2">
      <Card className="self-start lg:col-span-1">
        <CardHeader>
          <CardTitle>Company Details</CardTitle>
          <CardDescription>Set up your company brand.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* A flex gap rather than space-y: the hidden colors fieldset is the
              last child, and space-y would leave a gap under the uploads. */}
          <form
            id="service-form"
            onSubmit={form.handleSubmit(onSubmit)}
            onChange={clearApiError}
            className="flex flex-col gap-6"
          >
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Company name"
                maxLength={255}
                {...form.register("title")}
                aria-invalid={!!form.formState.errors.title}
                aria-describedby={
                  form.formState.errors.title ? "title-error" : undefined
                }
              />
              {form.formState.errors.title && (
                <p
                  id="title-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe your company..."
                maxLength={1000}
                rows={4}
                {...form.register("description")}
                aria-invalid={!!form.formState.errors.description}
                aria-describedby={
                  form.formState.errors.description
                    ? "description-error"
                    : undefined
                }
              />
              {form.formState.errors.description && (
                <p
                  id="description-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <Label htmlFor="url">URL</Label>
                <span className="ml-auto text-[13px] text-muted-foreground">Link to your web app</span>
              </div>
              <Input
                id="url"
                type="text"
                inputMode="url"
                placeholder="https://example.com"
                {...form.register("url")}
                aria-invalid={!!form.formState.errors.url}
                aria-describedby={
                  form.formState.errors.url ? "url-error" : undefined
                }
              />
              {form.formState.errors.url && (
                <p
                  id="url-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.url.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div>
                  <Label>
                    Logo <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-[13px] text-muted-foreground">512 x 512 px</p>
                </div>
                <Controller
                  name="logo"
                  control={form.control}
                  render={({ field }) => (
                    <ImageUpload
                      value={field.value ?? null}
                      onChange={field.onChange}
                      existingUrl={mode === "edit" ? service?.logo_url : undefined}
                      existingUrlCleared={logoCleared}
                      onClearExistingUrl={() => setLogoCleared(true)}
                      error={form.formState.errors.logo?.message}
                      isUploading={isUploadingLogo}
                      disabled={isSubmitting}
                      height="160px"
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <div>
                  <Label>Banner</Label>
                  <p className="text-[13px] text-muted-foreground">16:9 aspect ratio (1600 x 900 px)</p>
                </div>
                <Controller
                  name="banner"
                  control={form.control}
                  render={({ field }) => (
                    <ImageUpload
                      value={field.value ?? null}
                      onChange={field.onChange}
                      existingUrl={
                        mode === "edit" ? service?.banner_url : undefined
                      }
                      existingUrlCleared={bannerCleared}
                      onClearExistingUrl={() => setBannerCleared(true)}
                      error={form.formState.errors.banner?.message}
                      isUploading={isUploadingBanner}
                      disabled={isSubmitting}
                      height="160px"
                    />
                  )}
                />
              </div>
            </div>

            {/* NOTE: color settings are hidden for now; remove `hidden` to show them again */}
            <fieldset className="hidden space-y-4 border rounded-lg p-6">
              <legend className="text-[13px] pl-2 pr-2 font-medium mb-0">Colors</legend>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="text_color">Text Color</Label>
                  <Controller
                    name="text_color"
                    control={form.control}
                    render={({ field }) => (
                      <ColorPicker
                        id="text_color"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                        error={!!form.formState.errors.text_color}
                        aria-describedby={
                          form.formState.errors.text_color
                            ? "text_color-error"
                            : undefined
                        }
                      />
                    )}
                  />
                  {form.formState.errors.text_color && (
                    <p
                      id="text_color-error"
                      className="text-[13px] text-destructive"
                      role="alert"
                    >
                      {form.formState.errors.text_color.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="background_color">Background Color</Label>
                  <Controller
                    name="background_color"
                    control={form.control}
                    render={({ field }) => (
                      <ColorPicker
                        id="background_color"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                        error={!!form.formState.errors.background_color}
                        aria-describedby={
                          form.formState.errors.background_color
                            ? "background_color-error"
                            : undefined
                        }
                      />
                    )}
                  />
                  {form.formState.errors.background_color && (
                    <p
                      id="background_color-error"
                      className="text-[13px] text-destructive"
                      role="alert"
                    >
                      {form.formState.errors.background_color.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accent_color">Accent Color</Label>
                  <Controller
                    name="accent_color"
                    control={form.control}
                    render={({ field }) => (
                      <ColorPicker
                        id="accent_color"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                        error={!!form.formState.errors.accent_color}
                        aria-describedby={
                          form.formState.errors.accent_color
                            ? "accent_color-error"
                            : undefined
                        }
                      />
                    )}
                  />
                  {form.formState.errors.accent_color && (
                    <p
                      id="accent_color-error"
                      className="text-[13px] text-destructive"
                      role="alert"
                    >
                      {form.formState.errors.accent_color.message}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>

            {hasMutationError && (
              <FormError>
                <p>
                  {mutationError instanceof AxiosError &&
                  mutationError.response?.data?.error?.message
                    ? mutationError.response.data.error.message
                    : `Failed to ${isCreate ? "create" : "update"} details. Please try again.`}
                </p>
              </FormError>
            )}

            {hideHeader && <div className="pt-4">{submitButton}</div>}
          </form>
        </CardContent>
      </Card>

      <div className="flex justify-center lg:col-span-1">
        <ServicePreview service={previewData} showHeader />
      </div>
      </div>
    </div>
  );
}
